import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { Star, MapPin } from "lucide-react";
import { SERVICES, MANIFESTO, PRICING, AREAS, REVIEWS, IMAGES, BUSINESS } from "@/data";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 } }),
};

const SectionLabel = ({ children, testid }) => (
  <div data-testid={testid} className="mb-3 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-[#FF4E00]">
    <span className="h-px w-8 bg-[#FF4E00]" /> {children}
  </div>
);

export const EditorialMarquee = () => (
  <section className="border-b border-black bg-white py-6" data-testid="marquee-section">
    <Marquee speed={45} gradient={false}>
      {Array.from({ length: 6 }).map((_, i) => (
        <span key={i} className="font-anton text-5xl sm:text-7xl uppercase tracking-tight text-outline mx-6 flex items-center gap-6">
          Same-Day Service <span className="text-[#FF4E00] text-4xl">●</span> Pinellas County <span className="text-[#FF4E00] text-4xl">●</span> Open 6AM–6PM
        </span>
      ))}
    </Marquee>
  </section>
);

export const Services = () => (
  <section id="services" className="border-b border-black bg-white" data-testid="services-section">
    <div className="mx-auto max-w-[1400px] px-4 sm:px-8 py-20 lg:py-28">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={reveal}>
        <SectionLabel testid="services-label">What We Do</SectionLabel>
        <h2 className="font-anton text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tighter max-w-3xl">
          Haul it, dump it, done.
        </h2>
      </motion.div>
    </div>
    <div className="grid grid-cols-1 border-t border-black sm:grid-cols-2">
      {SERVICES.map((s, i) => (
        <motion.div
          key={s.tag}
          custom={i}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={reveal}
          data-testid={`service-card-${i}`}
          className="group relative border-b border-black sm:[&:nth-child(odd)]:border-r"
        >
          <div className="relative h-56 overflow-hidden border-b border-black">
            <img src={s.img} alt={s.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <span className="absolute left-0 top-0 bg-black px-4 py-2 font-anton text-lg text-white">{s.tag}</span>
          </div>
          <div className="p-6 lg:p-10 transition-colors group-hover:bg-[#FF4E00] group-hover:text-white">
            <h3 className="font-anton text-2xl lg:text-3xl uppercase tracking-tight">{s.title}</h3>
            <p className="mt-3 text-sm lg:text-base opacity-80">{s.desc}</p>
          </div>
        </motion.div>
      ))}
    </div>
  </section>
);

export const Manifesto = () => (
  <section className="border-b border-black bg-[#0A0A0A] text-white" data-testid="manifesto-section">
    <div className="mx-auto max-w-[1400px] px-4 sm:px-8 py-20 lg:py-28">
      <SectionLabel testid="manifesto-label">Why Moe's</SectionLabel>
      <div className="mt-10 grid grid-cols-1 gap-px lg:grid-cols-3">
        {MANIFESTO.map((m, i) => (
          <motion.div
            key={m.num}
            custom={i}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={reveal}
            data-testid={`manifesto-${i}`}
            className="border-t border-white/20 py-8 lg:border-t-0 lg:border-l lg:pl-8 lg:pr-6"
          >
            <div className="font-bebas text-8xl lg:text-9xl leading-none text-[#FF4E00]">{m.num}</div>
            <h3 className="font-anton text-4xl uppercase tracking-tight mt-2">{m.title}</h3>
            <p className="mt-4 max-w-xs text-white/70">{m.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export const Pricing = () => (
  <section id="pricing" className="border-b border-black bg-white" data-testid="pricing-section">
    <div className="mx-auto max-w-[1400px] px-4 sm:px-8 py-20 lg:py-28">
      <SectionLabel testid="pricing-label">Straight Pricing</SectionLabel>
      <h2 className="font-anton text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tighter">$100 to start.</h2>
      <p className="mt-4 max-w-lg text-muted-foreground">Just <b>$100 for the first 24 hours</b> and <b>only $35 each additional day</b>. Customers pay only the dump fees — no hidden charges, no surprises.</p>

      <div className="mt-12 border-t-2 border-black">
        {PRICING.map((p, i) => (
          <motion.div
            key={p.size}
            custom={i}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={reveal}
            data-testid={`pricing-row-${i}`}
            className="group flex flex-col gap-2 border-b border-black py-6 transition-colors hover:bg-black hover:text-white sm:flex-row sm:items-center sm:justify-between sm:px-4"
          >
            <div>
              <div className="font-anton text-2xl lg:text-3xl uppercase tracking-tight">{p.size}</div>
              <div className="text-sm text-muted-foreground group-hover:text-white/60">{p.best}</div>
            </div>
            <div className="font-bebas text-5xl lg:text-6xl leading-none text-[#FF4E00]">{p.price}</div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export const ServiceArea = () => (
  <section id="areas" className="border-b border-black" data-testid="areas-section">
    <div className="grid grid-cols-1 lg:grid-cols-2">
      <div className="relative min-h-[40vh] overflow-hidden border-b border-black lg:border-b-0 lg:border-r">
        <img src={IMAGES.tampaBay} alt="Aerial view of Pinellas County coastline" className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
        <div className="absolute bottom-0 left-0 bg-[#FF4E00] px-4 py-3 font-bebas text-3xl tracking-wide text-white">
          PINELLAS COUNTY, FL
        </div>
      </div>
      <div className="bg-white px-4 sm:px-8 py-20 lg:py-28">
        <SectionLabel testid="areas-label">Where We Serve</SectionLabel>
        <h2 className="font-anton text-5xl sm:text-6xl uppercase tracking-tighter">We come to you.</h2>
        <div className="mt-10 flex flex-wrap gap-x-2 gap-y-3">
          {AREAS.map((a, i) => (
            <motion.span
              key={a}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={reveal}
              data-testid={`area-${i}`}
              className="flex items-center gap-1 border border-black px-3 py-2 text-sm font-bold uppercase tracking-wide transition-colors hover:bg-[#FF4E00] hover:text-white hover:border-[#FF4E00]"
            >
              <MapPin size={13} /> {a}
            </motion.span>
          ))}
        </div>
        <p className="mt-8 text-muted-foreground">Not on the list? Give us a call — if you're near {BUSINESS.county}, we'll make it work.</p>
      </div>
    </div>
  </section>
);

export const Reviews = () => (
  <section id="reviews" className="border-b border-black bg-[#0A0A0A] text-white" data-testid="reviews-section">
    <div className="mx-auto max-w-[1400px] px-4 sm:px-8 py-20 lg:py-28">
      <div className="flex flex-col items-start gap-6 border-b border-white/20 pb-12 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <SectionLabel testid="reviews-label">The Word On The Street</SectionLabel>
          <div className="flex items-end gap-4">
            <span className="font-anton text-8xl lg:text-9xl leading-none text-[#FF4E00]">{BUSINESS.rating}</span>
            <div className="pb-3">
              <div className="flex gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={20} className="fill-[#FF4E00] text-[#FF4E00]" />
                ))}
              </div>
              <div className="mt-1 text-sm font-bold uppercase tracking-[0.2em]">{BUSINESS.reviewCount} Reviews</div>
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-px lg:grid-cols-3">
        {REVIEWS.map((r, i) => (
          <motion.div
            key={r.name}
            custom={i}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={reveal}
            data-testid={`review-${i}`}
            className="border-t border-white/20 py-10 lg:border-t-0 lg:border-l lg:px-8"
          >
            <div className="mb-4 flex gap-1">
              {Array.from({ length: 5 }).map((_, j) => (
                <Star key={j} size={14} className="fill-[#FF4E00] text-[#FF4E00]" />
              ))}
            </div>
            <p className="font-manrope text-xl leading-snug">“{r.text}”</p>
            <div className="mt-6 text-sm font-bold uppercase tracking-[0.15em] text-white/60">— {r.name}</div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
